import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { TRIGGER_BUILD } from '../graphql/mutations';
import { GET_BLUEPRINTS, GET_BLUEPRINT_VERSIONS } from '../graphql/queries';
import { getOsLabel, formatArch } from '../utils/os-icons';
import { PackerBuildIcon } from '../components/OsIcon';

export default function BuildTrigger() {
  const navigate = useNavigate();
  const { data: bpData } = useQuery(GET_BLUEPRINTS);
  const { data: versionData } = useQuery(GET_BLUEPRINT_VERSIONS);
  const [triggerBuild, { loading }] = useMutation(TRIGGER_BUILD);
  const [form, setForm] = useState({
    blueprintName: '',
    blueprintVersion: 'latest',
    imageType: 'golden_image',
    sourceAmiId: '',
    awsRegion: 'us-east-1',
    instanceType: 't3.medium',
  });

  const blueprints = bpData?.listBlueprints?.items || [];
  const versions = versionData?.listBlueprintVersions || [];
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleBlueprintChange = (e) => {
    const name = e.target.value;
    const bp = blueprints.find((b) => b.name === name);
    setForm({ ...form, blueprintName: name, blueprintVersion: bp?.version || 'latest' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.blueprintName) return;
    try {
      const { data } = await triggerBuild({ variables: { input: form } });
      const result = data.triggerBuild;
      if (result.status === 'failed') {
        toast.error(result.message || 'Build submission failed');
        return;
      }
      navigate(`/build/${result.workflowName}`);
    } catch (err) {
      toast.error(`Build failed: ${err.message}`);
    }
  };

  return (
    <div>
      <h2 className="page-title">Trigger Build</h2>
      <div className="card">
        {blueprints.length === 0 ? (
          <p>No blueprints available. <Link to="/blueprints" className="link">View blueprints</Link> to get started.</p>
        ) : (
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Blueprint</label>
              <select value={form.blueprintName} onChange={handleBlueprintChange}>
                <option value="">Select a blueprint...</option>
                {blueprints.map((bp) => (
                  <option key={bp.name} value={bp.name}>{getOsLabel(bp.name)} — {bp.name} ({formatArch(bp.architecture, true)})</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Version</label>
              <select value={form.blueprintVersion} onChange={set('blueprintVersion')}>
                <option value="latest">latest (synced from main)</option>
                {versions.map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Image Type</label>
              <select value={form.imageType} onChange={set('imageType')}>
                <option value="golden_image">Golden Image</option>
                <option value="base_image">Base Image</option>
                <option value="hardened_image">Hardened Image</option>
              </select>
            </div>
            <div className="form-group">
              <label>Source AMI ID (optional)</label>
              <input value={form.sourceAmiId} onChange={set('sourceAmiId')} placeholder="ami-..." />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>AWS Region</label>
              <select value={form.awsRegion} onChange={set('awsRegion')}>
                <option value="us-east-1">us-east-1</option>
                <option value="us-west-2">us-west-2</option>
                <option value="eu-west-1">eu-west-1</option>
                <option value="ap-southeast-2">ap-southeast-2</option>
              </select>
            </div>
            <div className="form-group">
              <label>Instance Type</label>
              <select value={form.instanceType} onChange={set('instanceType')}>
                <option value="t3.medium">t3.medium</option>
                <option value="t3.large">t3.large</option>
                <option value="m5.large">m5.large</option>
                <option value="c5.xlarge">c5.xlarge</option>
              </select>
            </div>
          </div>
          <button className="btn btn-primary" type="submit" disabled={loading || !form.blueprintName}>
            <PackerBuildIcon size={16} /> {loading ? 'Submitting...' : 'Start Build'}
          </button>
        </form>
        )}
      </div>
    </div>
  );
}
